import { createContext, memo, useContext, useMemo } from "react";
import * as THREE from "three";
import { Tube, Blob } from "./organs/shapes";
import { clipInside } from "../lib/clip";

const BONE = "#eee3cc";
const ART = "#c3262f";
export const FaintLayers = createContext(false);

const BoneMat = () => <meshStandardMaterial color={BONE} roughness={0.6} transparent opacity={useContext(FaintLayers) ? 0.12 : 0.35} depthWrite={false} onBeforeCompile={clipInside} />;
const ArtMat = () => <meshStandardMaterial color={ART} roughness={0.4} emissive={ART} emissiveIntensity={0.15} transparent opacity={useContext(FaintLayers) ? 0.3 : 0.7} depthWrite={false} onBeforeCompile={clipInside} />;

const add = (a, d) => [a[0] + d[0], a[1] + d[1], a[2] + d[2]];
const mix = (a, b, t) => a.map((v, i) => v + (b[i] - v) * t);

const Spine = ({ J }) => {
  const pts = useMemo(() => {
    const curve = new THREE.CatmullRomCurve3([J.head, J.neck, J["spine-1"], J["spine-2"], J["spine-3"], J["spine-4"], add(J.pelvis, [0, -0.04, -0.03])].map((p) => new THREE.Vector3(...add(p, [0, 0, -0.02]))));
    return curve.getSpacedPoints(26).map((v) => v.toArray());
  }, [J]);
  return pts.slice(1, -1).map((p, i) => (
    <mesh key={i} position={p}><cylinderGeometry args={[0.013, 0.013, 0.012, 12]} /><BoneMat /></mesh>
  ));
};

const Ribs = ({ J }) => {
  const top = J["spine-1"][1] + 0.06, bottom = J["spine-2"][1] - 0.02;
  return Array.from({ length: 10 }, (_, i) => {
    const y = top - ((top - bottom) * i) / 9;
    const w = 0.085 + Math.sin((i / 9) * Math.PI * 0.8 + 0.3) * 0.045;
    return (
      <mesh key={i} position={[0, y, 0.02]} rotation={[Math.PI / 2 + 0.35, 0, 0]} scale={[w, 0.085 + i * 0.002, 1]}>
        <torusGeometry args={[1, 0.05, 6, 40]} /><BoneMat />
      </mesh>
    );
  });
};

const Side = ({ J, s, d }) => {
  const g = (n) => J[`${s}-${n}`];
  const pelvisW = [d * 0.075, J.pelvis[1] + 0.005, J.pelvis[2] - 0.01];
  return (
    <group>
      <Tube pts={[[d * 0.02, g("clavicle")[1], 0.06], mix(g("clavicle"), g("shoulder"), 0.5), g("shoulder")]} r={0.006}><BoneMat /></Tube>
      <Blob pos={add(g("scapula"), [d * 0.02, -0.06, -0.08])} r={[0.045, 0.06, 0.01]} rot={[0.2, 0, d * 0.2]}><BoneMat /></Blob>
      <Tube pts={[g("shoulder"), mix(g("shoulder"), g("elbow"), 0.5), g("elbow")]} r={0.011}><BoneMat /></Tube>
      <Tube pts={[g("elbow"), g("hand")]} r={0.007}><BoneMat /></Tube>
      <Tube pts={[add(g("elbow"), [0, -0.012, 0.012]), add(g("hand"), [0, 0.008, 0.012])]} r={0.006}><BoneMat /></Tube>
      {["1", "3", "5"].map((f) => <Tube key={f} pts={[g("hand"), g(`finger-${f}-1`), g(`finger-${f}-4`)]} r={0.004}><BoneMat /></Tube>)}
      <Blob pos={pelvisW} r={[0.05, 0.055, 0.03]} rot={[0, d * 0.5, d * 0.35]}><BoneMat /></Blob>
      <Tube pts={[add(g("upper-leg"), [-d * 0.02, 0.005, 0]), g("upper-leg"), mix(g("upper-leg"), g("knee"), 0.5), g("knee")]} r={0.013}><BoneMat /></Tube>
      <Blob pos={add(g("knee"), [0, 0, 0.045])} r={[0.02, 0.024, 0.01]}><BoneMat /></Blob>
      <Tube pts={[g("knee"), mix(g("knee"), g("ankle"), 0.5), g("ankle")]} r={0.011}><BoneMat /></Tube>
      <Tube pts={[add(g("knee"), [d * 0.025, -0.02, -0.01]), add(g("ankle"), [d * 0.02, 0.01, -0.01])]} r={0.005}><BoneMat /></Tube>
      <Tube pts={[add(g("ankle"), [0, -0.03, -0.04]), g("ankle"), g("toe-1-3")]} r={0.006}><BoneMat /></Tube>
      <Tube pts={[g("ankle"), g("toe-5-3")]} r={0.005}><BoneMat /></Tube>
    </group>
  );
};

export const Skeleton = memo(({ J }) => {
  const skullC = [0, (J.head[1] + J["head-2"][1]) / 2 + 0.005, J.head[2] - 0.005];
  return (
    <group>
      <Blob pos={skullC} r={[0.072, 0.082, 0.094]}><BoneMat /></Blob>
      <Tube pts={[[0.058, J.head[1], J.head[2] - 0.005], [0.048, J.jaw[1] + 0.005, J.jaw[2] - 0.03], [0, J.jaw[1], J.jaw[2] - 0.005], [-0.048, J.jaw[1] + 0.005, J.jaw[2] - 0.03], [-0.058, J.head[1], J.head[2] - 0.005]]} r={0.008}><BoneMat /></Tube>
      <Spine J={J} />
      <Ribs J={J} />
      <mesh position={[0, (J["spine-1"][1] + J["spine-2"][1]) / 2 + 0.02, 0.105]} rotation={[-0.15, 0, 0]}><boxGeometry args={[0.025, 0.15, 0.008]} /><BoneMat /></mesh>
      <Blob pos={add(J.pelvis, [0, -0.02, -0.06])} r={[0.035, 0.05, 0.02]} rot={[0.4, 0, 0]}><BoneMat /></Blob>
      <Side J={J} s="l" d={1} />
      <Side J={J} s="r" d={-1} />
    </group>
  );
});

export const Arteries = memo(({ J }) => {
  const s1 = J["spine-1"], s4 = J["spine-4"];
  const arch = [0.012, s1[1] + 0.02, 0.035];
  const fork = [0, s4[1] - 0.04, s4[2] + 0.05];
  const limb = (d) => {
    const g = (n) => J[`${d > 0 ? "l" : "r"}-${n}`];
    return [
      [arch, [d * 0.03, s1[1] + 0.06, 0.04], [d * 0.025, J.neck[1], J.neck[2] + 0.04], [d * 0.04, J.jaw[1], J.jaw[2] - 0.07], [d * 0.055, J.head[1] + 0.04, J.head[2]]],
      [arch, [d * 0.06, s1[1] + 0.07, 0.03], add(g("shoulder"), [0, -0.025, 0.01]), mix(g("shoulder"), g("elbow"), 0.5), add(g("elbow"), [0, 0, 0.02]), g("hand"), g("finger-3-4")],
      [fork, [d * 0.05, J.pelvis[1] - 0.04, 0.04], add(g("upper-leg"), [-d * 0.02, -0.03, 0.045]), mix(g("upper-leg"), g("knee"), 0.5), add(g("knee"), [0, 0, -0.03]), g("ankle"), g("toe-3-3")],
    ];
  };
  const paths = [...limb(1), ...limb(-1)];
  return (
    <group>
      <Tube pts={[[0.02, s1[1] - 0.06, 0.06], arch, [0.015, s1[1] + 0.035, 0.0], [0.012, s1[1], s1[2] + 0.045], add(J["spine-2"], [0.012, 0, 0.05]), add(J["spine-3"], [0.008, 0, 0.05]), fork]} r={0.0065} seg={96}><ArtMat /></Tube>
      {paths.map((p, i) => <Tube key={i} pts={p} r={0.0038} seg={120}><ArtMat /></Tube>)}
    </group>
  );
});
